import type React from "react";
import { useState } from "react";
import { twJoin } from "tailwind-merge";

import type { VideoDto } from "@/api/types";
import ProcessingVideoBackground from "@/assets/images/processing-video-background.png";
import { buttonVariants } from "@/design-system/Button";
import { MediaSkeleton } from "@/design-system/MediaSkeleton";
import { Spinner } from "@/design-system/Spinner";
import { twResolve } from "@/helpers/tw-resolve";
import { useBool } from "@/hooks/useBool";
import { useTranslation } from "@/translations";
import { RuntimeAspectRatio } from "@/ui-adapters/RuntimeAspectRatio";

import { getAspectRatio } from "../CommunityFeed/CommunityPostMedia/helpers";
import { PlayIcon } from "../Icons/PlayIcon";
import { VideoFilePreview } from "../VideoFilePreview/VideoFilePreview";
import { VideoModal } from "../VideoModal/VideoModal";

interface VideoProps {
  video: VideoDto;
  shouldLoad?: boolean;
  onOpen?: (videoId: string) => void;
  showViewCount?: boolean;
}

export function Video({ video, shouldLoad = true, onOpen, showViewCount }: VideoProps): React.ReactNode {
  const [isVideoModalOpened, videoModalOpenHandlers] = useBool();
  const [isLoadingThumbnail, setIsLoadingThumbnail] = useState(true);
  const { t } = useTranslation();

  // Edge case: Video was not required to be processed
  // Should NOT happen in production
  if (video.processingStatus === "skipped") {
    return (
      <VideoFilePreview
        {...{
          video,
        }}
      />
    );
  }

  if (video.processingStatus === "processed" && video.thumbnail) {
    const thumbnail = video.thumbnail;
    const thumbnailSrc = shouldLoad ? thumbnail.url : undefined;
    const handleOpenVideo = () => {
      videoModalOpenHandlers.setTrue();
      onOpen?.(video.id);
    };

    return (
      <>
        <button
          type="button"
          onClick={handleOpenVideo}
          className={twResolve(buttonVariants({ variant: "ghostSecondary", type: "button" }), "w-full @md:w-fit")}
        >
          <RuntimeAspectRatio aspectRatio={getAspectRatio(thumbnail)}>
            {(aspectRatioProps) => (
              <div
                {...aspectRatioProps}
                className="relative max-h-media-display-compact w-full max-w-full cursor-pointer overflow-hidden rounded-md bg-background-default-inverse transition-opacity hover:opacity-90 @md:max-h-media-display-wide @md:w-fit"
              >
                {isLoadingThumbnail && <MediaSkeleton />}

                {/* Overlay */}
                {!isLoadingThumbnail && (
                  <div className="absolute inset-0 z-raised flex items-center justify-center bg-background-default-inverse/20">
                    <span className="flex size-control-md items-center justify-center rounded-full bg-background-default-inverse">
                      <span className="ml-0.5 fill-text-default-inverse">
                        <PlayIcon size={20} />
                      </span>
                    </span>

                    <div className="absolute bottom-2 flex w-full flex-wrap items-center gap-space-md px-space-sm text-caption text-text-default-inverse [&>span]:rounded-lg [&>span]:bg-background-default-inverse [&>span]:px-space-sm [&>span]:py-space-xs">
                      {showViewCount && (
                        <span>
                          {t("common.label.view", {
                            count: video.viewCount || 0,
                          })}
                        </span>
                      )}
                      {video.formattedDuration && <span className="ml-auto">{video.formattedDuration}</span>}
                    </div>
                  </div>
                )}

                {/* Thumbnail */}
                <img
                  className={twJoin(
                    "max-h-full max-w-full object-contain",
                    isLoadingThumbnail ? "opacity-0" : "opacity-100 transition-opacity hover:opacity-90",
                  )}
                  src={thumbnailSrc}
                  alt={thumbnail.description}
                  onLoad={() => setIsLoadingThumbnail(false)}
                  width={thumbnail.metadata.width}
                  height={thumbnail.metadata.height}
                />
              </div>
            )}
          </RuntimeAspectRatio>
        </button>

        <VideoModal isOpened={isVideoModalOpened} videoSrc={video.url} onOpenChange={videoModalOpenHandlers.set} />
      </>
    );
  }

  // Video is being processed, no thumbnail available
  return (
    <figure className="relative aspect-video overflow-hidden rounded-lg">
      <img
        src={ProcessingVideoBackground}
        alt={t("component.video-preview.placeholder-image.alt")}
        className="size-full object-cover"
      />
      <div className="absolute inset-0 z-raised flex items-center justify-center bg-background-default-inverse/40 backdrop-blur-md">
        <span className="flex size-control-md items-center justify-center rounded-full bg-background-default-inverse">
          <Spinner size="sm" tone="inverse" />
        </span>
        <p className="absolute inset-x-space-md bottom-2 rounded-md bg-background-default-inverse p-space-xs text-center text-caption text-text-default-inverse @md:bottom-4 @md:p-space-sm @md:text-body">
          {t("component.video-preview.processing.description")}
        </p>
      </div>
    </figure>
  );
}
